/** jsdom объявляет SVG-интерфейсы, но не реализует геометрию: ни матриц, ни измерения текста. */

const CHAR_WIDTH = 6.2;
const LINE_HEIGHT = 14;

class Matrix {
	constructor(
		public a = 1,
		public b = 0,
		public c = 0,
		public d = 1,
		public e = 0,
		public f = 0,
	) {}

	multiply(o: Matrix): Matrix {
		return new Matrix(
			this.a * o.a + this.c * o.b,
			this.b * o.a + this.d * o.b,
			this.a * o.c + this.c * o.d,
			this.b * o.c + this.d * o.d,
			this.a * o.e + this.c * o.f + this.e,
			this.b * o.e + this.d * o.f + this.f,
		);
	}

	translate(x: number, y: number): Matrix {
		return this.multiply(new Matrix(1, 0, 0, 1, x, y));
	}

	scale(k: number): Matrix {
		return this.multiply(new Matrix(k, 0, 0, k, 0, 0));
	}

	inverse(): Matrix {
		const det = this.a * this.d - this.b * this.c;
		return new Matrix(
			this.d / det,
			-this.b / det,
			-this.c / det,
			this.a / det,
			(this.c * this.f - this.d * this.e) / det,
			(this.b * this.e - this.a * this.f) / det,
		);
	}
}

/** diagram-js двигает фигуры сеттерами, не матрицей: `diagram-js/lib/util/SvgTransformUtil.js`. */
class Transform {
	constructor(public matrix: Matrix = new Matrix()) {}

	setMatrix(m: Matrix) {
		this.matrix = m;
	}

	setTranslate(x: number, y: number) {
		this.matrix = new Matrix(1, 0, 0, 1, x, y);
	}

	setScale(x: number, y: number) {
		this.matrix = new Matrix(x, 0, 0, y, 0, 0);
	}

	setRotate(angle: number) {
		const radians = (angle * Math.PI) / 180;
		const cos = Math.cos(radians);
		const sin = Math.sin(radians);
		this.matrix = new Matrix(cos, sin, -sin, cos, 0, 0);
	}
}

/** Отражение списка в атрибут `transform` браузер делает сам, jsdom — нет. */
class TransformList {
	private _items: Transform[] = [];

	constructor(private _node: Element) {}

	get numberOfItems() {
		return this._items.length;
	}

	getItem(index: number) {
		return this._items[index];
	}

	clear() {
		this._items = [];
		this._writeAttribute();
	}

	appendItem(transform: Transform) {
		this._items.push(transform);
		this._writeAttribute();
		return transform;
	}

	createSVGTransformFromMatrix(matrix: Matrix) {
		return new Transform(matrix);
	}

	consolidate(): Transform | null {
		if (!this._items.length) return null;
		return new Transform(this._items.reduce((acc, item) => acc.multiply(item.matrix), new Matrix()));
	}

	private _writeAttribute() {
		const consolidated = this.consolidate();
		if (!consolidated) return this._node.removeAttribute("transform");
		const { a, b, c, d, e, f } = consolidated.matrix;
		const round = (n: number) => Math.round(n * 1e6) / 1e6;
		this._node.setAttribute("transform", `matrix(${[a, b, c, d, e, f].map(round).join(" ")})`);
	}
}

/** `transform` есть и в jsdom, а `createSVGMatrix` — только там, где геометрия настоящая. */
export const isSvgDomComplete = (): boolean =>
	typeof SVGSVGElement !== "undefined" &&
	typeof SVGSVGElement.prototype.createSVGMatrix === "function" &&
	typeof CSS !== "undefined";

const transformLists = new WeakMap<Element, TransformList>();

const installSvgDomShim = () => {
	if (typeof SVGElement === "undefined" || isSvgDomComplete()) return;

	const proto = SVGElement.prototype as unknown as Record<string, unknown>;
	const globals = globalThis as unknown as Record<string, unknown>;

	globals.SVGMatrix = Matrix;
	globals.SVGTransform = Transform;
	if (typeof CSS === "undefined") {
		globals.CSS = { escape: (value: string) => String(value).replace(/([^\w-])/g, "\\$1") };
	}

	Object.defineProperty(SVGElement.prototype, "transform", {
		configurable: true,
		get(this: Element) {
			let list = transformLists.get(this);
			if (!list) {
				list = new TransformList(this);
				transformLists.set(this, list);
			}
			return { baseVal: list, animVal: list };
		},
	});

	proto.createSVGMatrix = () => new Matrix();
	proto.createSVGTransform = () => new Transform();
	proto.createSVGPoint = () => ({ x: 0, y: 0, matrixTransform: (m: Matrix) => ({ x: m.e, y: m.f }) });
	proto.getCTM = function (this: Element & { transform: { baseVal: TransformList } }) {
		return this.transform.baseVal.consolidate()?.matrix ?? new Matrix();
	};
	proto.getScreenCTM = proto.getCTM;
	proto.suspendRedraw = () => 0;
	proto.unsuspendRedrawAll = () => undefined;
	proto.forceRedraw = () => undefined;

	proto.getBBox = function (this: Element) {
		return { x: 0, y: 0, width: (this.textContent || "").length * CHAR_WIDTH, height: LINE_HEIGHT };
	};
	proto.getComputedTextLength = function (this: Element) {
		return (this.textContent || "").length * CHAR_WIDTH;
	};
	proto.getSubStringLength = (from: number, length: number) => (from >= 0 ? length : 0) * CHAR_WIDTH;
	proto.getNumberOfChars = function (this: Element) {
		return (this.textContent || "").length;
	};
};

export default installSvgDomShim;
